import { zodResolver } from '@hookform/resolvers/zod';
import { useClickOutside } from '@react-hooks-library/core';
import { useDependency, useOptionalDependency } from 'ditox-react';
import { Add, ArrowCircleUp2, Grammerly, Send, Trash } from 'iconsax-react';
import { head } from 'lodash-es';
import { useSubscription } from 'observable-hooks';
import { type PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate } from 'react-router';
import { useTypedParams } from 'react-router-typesafe-routes';
import { Virtuoso } from 'react-virtuoso';
import { z } from 'zod';
import KeyboardIcon from '@/assets/images/icons/keyboard.svg?react';
import MicrophoneIcon from '@/assets/images/icons/microphone.svg?react';
import PopupArrow from '@/assets/images/icons/popup-arrow.svg?react';
import AppCell from '@/components/common/app-cell';
import { AvatarWithStatus } from '@/components/common/avatar-with-status';
import DurationDisplay from '@/components/common/duration-display';
import ProgressButton from '@/components/common/progress-button';
import ConversationAttachmentMenu from '@/components/conversations/attachment-form/conversation-attachment-menu';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import ForwardForm, { transformToSelectedMap } from '@/components/scan-code/forward-form';
import { components } from '@/components/sessions/session-list/forward-session-list';
import ForwardSessionListItem from '@/components/sessions/session-list/forward-session-list-item';
import SymbolMenu, { type SymbolMenuActions } from '@/components/symbol/symbol-menu';
import { QUILL_MANAGER_TOKEN, TRANSCODER_TOKEN } from '@/container';
import {
  type BatchMessageRequest,
  batchMessageRequestSchema,
  useSendBatchMessage,
} from '@/hooks/batch-message/use-create-batch-message';
import { getBatchMessagesMap } from '@/hooks/batch-message/use-get-batch-messages';
import type { ForwardListItem } from '@/hooks/contacts/use-forward-list';
import useAudioRecorder from '@/hooks/conversations/use-audio-recorder';
import usePickMediaFile from '@/hooks/files/use-pick-media-file';
import useSystemSetting from '@/hooks/files/use-system-setting';
import useUploadFile from '@/hooks/files/use-upload-file';
import type { ForwardTarget } from '@/hooks/forward/use-batch-forward';
import useMediaSupport from '@/hooks/settings/use-media-support';
import useAdjustLayout from '@/hooks/use-adjust-layout';
import useUserPlatform from '@/hooks/use-user-platform';
import useBlacklist from '@/hooks/users/use-blacklist';
import { useIsInternalUser } from '@/hooks/users/use-user-info';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import { uploadableScheme } from '@/models/uploadable';
import ROUTES, { type PromiseLoaderReturnType } from '@/routes';
import { useDeviceStore } from '@/stores/device';
import { makePrompt } from '@/stores/prompter';
import { addToast } from '@/stores/toast';
import { readableFileSize } from '@/utils/file-size';
import { FileType } from '@/utils/file-type';
import cn from '@/utils/merge-class-names';
import { convertBlobToUploadable, convertFileToUploadable, convertUploadableToFile } from '@/utils/uploadable';

const CreateBatchMessageRoute = () => {
  const loaderData = useLoaderData<PromiseLoaderReturnType>();
  const { batchId } = useTypedParams(ROUTES.MY.SETTINGS.MESSAGING.BATCH.CREATE.BY_ID);
  const step = useStep(2, batchId ? 2 : 1);
  const { t } = useTranslation();
  const { isDesktop } = useUserPlatform();
  const [forwardTargets, setForwardTargets] = useState<ForwardTarget[] | null>(null);
  const [showAllForwardTarget, setShowAllForwardTarget] = useState(false);
  const batchMessageMap = getBatchMessagesMap();
  const { mutateAsync } = useSendBatchMessage();
  const { upload } = useUploadFile();
  const { data: blockedData } = useBlacklist();
  const navigate = useNavigate();
  const transcoder = useOptionalDependency(TRANSCODER_TOKEN);
  const batchMessage = batchMessageMap[batchId ?? 0];

  const initialForwardTarget = useMemo(
    () =>
      batchMessage?.Recipients.reduce<{
        userIds: number[];
        groupIds: number[];
        tagIds: number[];
      }>(
        (acc, curr) => {
          if (curr.GroupId) {
            acc.groupIds.push(curr.GroupId);
          }
          if (curr.UserId) {
            acc.userIds.push(curr.UserId);
          }
          return acc;
        },
        { userIds: [], groupIds: [], tagIds: [] },
      ),
    [batchId],
  );

  useEffect(() => {
    (async () => {
      const map = await transformToSelectedMap(initialForwardTarget);
      setForwardTargets(Array.from(map.values()));
    })();
  }, [initialForwardTarget]);

  const areAllReceiversBlocked = useMemo(() => {
    const targetUserIds = batchMessage?.Recipients.map(({ UserId }) => UserId).filter((id) => !!id) ?? [];

    const blockedUserIds = blockedData?.map(({ BlackUserID }) => BlackUserID) ?? [];

    if (targetUserIds.length === 0 || blockedUserIds.length === 0) return false;

    const blockedSet = new Set(blockedUserIds);

    return targetUserIds.every((id) => blockedSet.has(id));
  }, [batchId, blockedData]);

  useEffect(() => {
    if (areAllReceiversBlocked) {
      navigate(ROUTES.MY.SETTINGS.MESSAGING.BATCH.$path(), { replace: true });
    }
  }, [areAllReceiversBlocked]);

  return (
    <LoadingLayout
      className='h-full'
      promise={loaderData?.promises}>
      {() => (
        <>
          <ChatInputForm
            onClickDescription={() => setShowAllForwardTarget(true)}
            isVisible={step.current === 2 && !showAllForwardTarget}
            description={forwardTargets?.map((s) => s.name).join(',')}
            navigateBack={() => {
              step.prev();
            }}
            onSubmit={async (v) => {
              await makePrompt({
                key: 'batch-message',
                type: 'dialog',
                title: t('messaging-settings.batch-message.sending-confirmation'),

                promise: async () => {
                  const result: Partial<BatchMessageRequest> = { TagIds: [], GroupIds: [], UserIds: [] };

                  forwardTargets?.forEach((t) => {
                    switch (t.type) {
                      case 'group':
                        (result.GroupIds ?? []).push(t.groupId);
                        break;
                      case 'tag':
                        (result.TagIds ?? []).push(t.tagId);
                        break;
                      case 'user':
                        (result.UserIds ?? []).push(t.userId);
                    }
                  });

                  if (v.attachment && v.attachmentType) {
                    let uploadable = v.attachment;
                    // NOTE: Converts any audio-containing file (e.g., .webm, .ogg, .mp4, .wav) into a standardized .mp3 file
                    if (v.attachmentType === FileType.Audio && transcoder) {
                      const file = await convertUploadableToFile(uploadable);
                      uploadable = convertFileToUploadable(await transcoder.transcodeAudio(file));
                    }
                    const url = await upload(uploadable, v.attachmentType.split('/')[0] ?? '');

                    const { name, size } = v.attachment;

                    switch (v.attachmentType) {
                      case FileType.Audio:
                        result.MsgType = ConversationMessageContentType.Audio;
                        // attachmentInfo is audio duration
                        result.Message = [url, v.attachmentInfo].join('|');
                        break;
                      case FileType.Image:
                        result.MsgType = ConversationMessageContentType.Image;
                        result.Message = url;
                        break;
                      case FileType.File:
                        result.MsgType = ConversationMessageContentType.File;
                        result.Message = [url, name, readableFileSize(size)].join('|');
                        break;
                      case FileType.Video:
                        result.MsgType = ConversationMessageContentType.Video;
                        result.Message = url;
                        break;
                      default:
                        result.Message = url;
                    }
                  }

                  if (v.message && v.msgType) {
                    result.MsgType = v.msgType;
                    result.Message = v.message;
                  }

                  const request = batchMessageRequestSchema.safeParse(result);

                  if (request.data) {
                    const response = await mutateAsync(request.data);
                    if (response.Code === 0) {
                      navigate(ROUTES.MY.SETTINGS.MESSAGING.BATCH.$path(), { replace: true });
                    }
                  }
                },
              });
            }}
          />
          <PageLayout
            className={cn('h-full', { hidden: !showAllForwardTarget })}
            headerConfig={{
              navigateBack: () => {
                setShowAllForwardTarget(false);
              },
            }}>
            <Virtuoso<ForwardListItem>
              data={forwardTargets ?? []}
              fixedItemHeight={56}
              className={cn('flex-1')}
              computeItemKey={(_, item) => item.key}
              components={components}
              itemContent={(_, item) =>
                item.type === 'tag' ? (
                  <div className=' flex flex-row items-center py-2 px-5 gap-4'>
                    <AvatarWithStatus
                      isOnline={false}
                      className='size-12'
                      defaultAvatar='tag'
                      size={[48, 48]}
                      imageUrl={item.imageURL}
                      label={item.name}
                    />
                    <p>{item.name}</p>
                  </div>
                ) : (
                  <ForwardSessionListItem
                    showCheckBox={false}
                    className='pointer-events-none'
                    session={item}
                    onSelectSession={() => {}}
                    isSelected={false}
                  />
                )
              }
            />
          </PageLayout>
          <ForwardForm
            tabOptions={['friends', 'groups', 'recent', 'tags']}
            key={batchId}
            initialValue={initialForwardTarget}
            navigateBack={!isDesktop}
            className={cn('h-full', { hidden: step.current !== 1 || showAllForwardTarget })}
            title={t('messaging-settings.batch-message.select-contact')}
            buttonLabel='common.confirm'
            onForward={(value) => {
              setForwardTargets(value);
              step.next();
            }}
          />
        </>
      )}
    </LoadingLayout>
  );
};

export default CreateBatchMessageRoute;

const useStep = (totalStep: number, init = 1) => {
  const [currentStep, setCurrentStep] = useState(init);

  return {
    current: currentStep,
    reset: () => {
      setCurrentStep(init);
    },
    next: () => {
      setCurrentStep((prev) => {
        if (prev < totalStep) return prev + 1;
        return prev;
      });
    },
    prev: () => {
      setCurrentStep((prev) => {
        if (prev > 1) return prev - 1;
        return prev;
      });
    },
  };
};

const chatInputFormSchema = z.object({
  message: z.string().optional(),
  msgType: z.nativeEnum(ConversationMessageContentType).optional(),
  attachmentType: z.nativeEnum(FileType).optional(),
  attachment: uploadableScheme.optional(),
  attachmentInfo: z.string().optional(),
});
type ChatInputFormValue = z.infer<typeof chatInputFormSchema>;

const ChatInputForm = ({
  isLoading,
  isVisible,
  className,
  description,
  navigateBack,
  initialValue,
  onSubmit,
  onClickDescription,
}: PropsWithChildren<{
  isLoading?: boolean;
  isVisible: boolean;
  className?: string;
  description?: string;
  navigateBack?: string | boolean | (() => void);
  initialValue?: ChatInputFormValue;
  onSubmit: (value: ChatInputFormValue) => void;
  onClickDescription: () => void;
}>) => {
  const quill = useDependency(QUILL_MANAGER_TOKEN);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const symbolMenuRef = useRef<SymbolMenuActions>(null);
  const attachmentMenuRef = useRef<HTMLDivElement>(null);
  const cursorIdxRef = useRef<number | null>(null);

  const [isSymbolMenuOpen, setIsSymbolMenuOpen] = useState(false);
  const [isRecorderOpen, setIsRecorderOpen] = useState(false);
  const [isAttachmentMenuOpen, setIsAttachmentMenuOpen] = useState(false);

  const { enterAsSubmit, alwaysEnterAsSubmit } = useDeviceStore();
  const { isDesktop } = useUserPlatform();
  const { t } = useTranslation();

  const { isInternalUser } = useIsInternalUser();
  const { getFileSizeLimit } = useSystemSetting();

  const { ref: fileInputRef, pickMedia, clearMedia } = usePickMediaFile();

  const form = useForm<ChatInputFormValue>({
    reValidateMode: 'onSubmit',
    resolver: zodResolver(chatInputFormSchema),
    defaultValues: initialValue,
  });

  const { field: filesField } = useController({ control: form.control, name: 'attachment' });

  useAdjustLayout({
    inputTargetSelector: '#quill-editor .ql-editor',
  });

  useEffect(() => {
    if (containerRef.current) {
      quill.initialize(containerRef.current, t('conversation.placeholder'), []);
    }

    return () => {
      quill.dispose();
    };
  }, [quill, t]);

  useEffect(() => {
    quill.enterAsSubmit = isDesktop || alwaysEnterAsSubmit || Boolean(enterAsSubmit);
    if (isDesktop || alwaysEnterAsSubmit) quill.focus(cursorIdxRef.current ?? quill.initialContent?.length ?? 0);
  }, [enterAsSubmit, isDesktop, quill, alwaysEnterAsSubmit, isVisible]);

  useClickOutside(attachmentMenuRef, () => {
    setIsAttachmentMenuOpen(false);
  });
  const handleSubmitText = (value: ChatInputFormValue) => {
    onSubmit({ message: value.message, msgType: ConversationMessageContentType.Text });
  };

  useSubscription(quill.event$, (event) => {
    switch (event.action) {
      case 'focus':
        if (event.data) {
          setIsSymbolMenuOpen(false);
          cursorIdxRef.current = quill.getSelection()?.index ?? 0;
        }
        break;
      case 'submit': {
        const textMsg = form.getValues('message');
        if (form.formState.isSubmitting || !textMsg) return;
        if (cursorIdxRef.current !== null && cursorIdxRef.current < (textMsg?.trimEnd().length ?? 0)) {
          quill.initialContent = textMsg;
          quill.focus(cursorIdxRef.current + 1);
          return;
        }
        form.handleSubmit(handleSubmitText)();

        break;
      }
      case 'content':
        if (event.data?.trimEnd()) {
          cursorIdxRef.current = quill.getSelection()?.index ?? 0;
          form.setValue('msgType', ConversationMessageContentType.Text);
          form.setValue('message', event.data.trimEnd());
        } else {
          form.reset();
        }
        break;
      case 'draft': {
        const textMsg = form.getValues('message');
        if (!textMsg) return;
        quill.initialContent = textMsg;
      }
    }
  });

  const { recordableMediaType } = useMediaSupport();

  const { isRecording, duration, canRecord, stopRecording, startRecording, cancelRecording } = useAudioRecorder(
    (blob, duration) => {
      onSubmit({
        attachmentType: FileType.Audio,
        attachment: convertBlobToUploadable(blob, 'recording.mp3'),
        attachmentInfo: duration.toString(),
      });
    },
    {
      audioTrackConstraints: {
        channelCount: 1,
        noiseSuppression: true,
        echoCancellation: true,
      },
      mediaRecorderOptions: {
        mimeType: recordableMediaType ?? 'audio/webm;codecs=opus',
      },
    },
  );

  return (
    <PageLayout
      className={cn(
        'pb-safe-or-0 has-[.ql-editor:focus]:mode-standalone:pb-0',
        'flex flex-col',
        'desktop:h-full',
        { hidden: !isVisible },
        className,
      )}
      headerConfig={{ navigateBack }}
      title={t('messaging-settings.batch-message.send')}>
      <AppCell
        chevron
        className='bg-neutral-0'
        onClick={() => {
          onClickDescription();
        }}>
        <div className='flex flex-col  text-body1 flex-1'>
          <p className='text-secondary mb-1'>{t('messaging-settings.batch-message.send-to')}</p>
          <p className='line-clamp-1 break-all'>{description}</p>
        </div>
      </AppCell>
      <div className='bg-base-100 flex-1' />
      <div className='flex flex-col items-stretch desktop:relative bg-neutral-0'>
        <form
          className='flex flex-row flex-wrap items-center bg-white p-2 gap-2'
          onSubmit={form.handleSubmit(handleSubmitText)}>
          <button
            type='button'
            className={cn('relative btn btn-square btn-ghost btn-sm desktop:self-center', {
              hidden: isRecorderOpen,
            })}
            onClick={() => {
              setIsAttachmentMenuOpen((prev) => !prev);
              setIsRecorderOpen(false);
            }}>
            {isAttachmentMenuOpen && <span className='absolute inset-0 z-10 hidden desktop:block' />}
            <Add
              className={cn('size-6 transition-transform', {
                'rotate-45': isAttachmentMenuOpen,
              })}
            />
          </button>
          <div
            onContextMenu={(e) => e.preventDefault()}
            className={cn('flex flex-1 items-center justify-between gap-1', { hidden: !isRecorderOpen })}>
            <button
              type='button'
              className='btn btn-sm btn-circle btn-ghost'
              onClick={() => {
                if (isRecording) cancelRecording();
                setIsRecorderOpen(false);
              }}>
              <Trash className='size-6' />
            </button>

            {canRecord ? (
              <div className='indicator relative size-full flex-1 select-none justify-end'>
                <div className='flex w-full items-center justify-center gap-2'>
                  {isLoading ? (
                    <span className='loading loading-spinner loading-sm' />
                  ) : (
                    <>
                      <span
                        className={cn('size-1.5 rounded-full bg-neutral-400 transition-colors', {
                          'bg-error text-error/50 animate-record-pulse': isRecording,
                        })}
                      />
                      <DurationDisplay
                        withUnits={false}
                        duration={duration}
                      />
                    </>
                  )}
                </div>
              </div>
            ) : (
              <p className='text-sm text-center text-error'>{t('errors.no-microphone')}</p>
            )}

            <div className={cn('flex items-center', { hidden: isLoading })}>
              <button
                type='button'
                onClick={async () => {
                  if (isRecording) {
                    await stopRecording();
                  } else {
                    startRecording();
                  }
                }}
                className='relative mx-2 min-w-max rounded bg-base-200 px-3 py-2 text-h3 desktop:text-xs'>
                {isRecording ? t('conversation.recording.send') : t('conversation.recording.record')}
                <PopupArrow className='absolute -right-3 top-1/2 size-5 -translate-y-1/2 -rotate-90 text-base-200' />
              </button>

              <button
                type='button'
                className={cn('btn btn-circle btn-sm p-0 btn-ghost transition-transform text-primary', {
                  'rotate-90': !isRecording,
                })}
                onClick={async () => {
                  if (isRecording) {
                    await stopRecording();
                  } else {
                    startRecording();
                  }
                }}>
                <ArrowCircleUp2
                  variant='Bold'
                  className='size-8'
                />
              </button>
            </div>
          </div>
          <div
            className={cn(
              'flex min-h-10 w-full flex-1 items-center transition-colors desktop:gap-4 textarea bg-base-100  ring-primary border-none overflow-hidden p-0 ',
              { hidden: isRecorderOpen },
            )}>
            <div
              id='quill-editor'
              ref={containerRef}
              className='h-full max-h-36 flex-1 overflow-auto break-all !border-none !text-base !outline-none '
            />
            <button
              type='button'
              onMouseDown={(e) => {
                e.preventDefault();
                setIsSymbolMenuOpen(!isSymbolMenuOpen);
                if (isSymbolMenuOpen) {
                  quill.focus();
                } else {
                  quill.blur();
                }
              }}
              className='btn btn-square btn-ghost btn-sm size-8'>
              {isSymbolMenuOpen ? (
                <KeyboardIcon className='size-6 text-body' />
              ) : (
                <Grammerly className='size-6 text-body' />
              )}
            </button>
          </div>
          {form.watch('message') ? (
            <ProgressButton
              as='submit'
              isLoading={form.formState.isSubmitting || isLoading}
              onPointerDown={(e) => e?.preventDefault()}
              onMouseDown={(e) => e?.preventDefault()}
              tabIndex={0}
              icon={
                <Send
                  variant='Bold'
                  className='size-6 text-primary'
                />
              }
              className='btn btn-square btn-ghost btn-sm ring-0'
            />
          ) : (
            <button
              type='button'
              onClick={() => setIsRecorderOpen(true)}
              className='btn btn-square btn-ghost btn-sm ring-0'>
              <MicrophoneIcon className='size-6' />
            </button>
          )}
        </form>
        <SymbolMenu
          className='desktop:w-[480px] desktop:left-0'
          ref={symbolMenuRef}
          isOpen={isSymbolMenuOpen}
          onClose={() => setIsSymbolMenuOpen(false)}
          onEmoji={(emoji) => quill.insertText(emoji, undefined, false)}
          onSticker={(sticker) => {
            onSubmit({ message: sticker, msgType: ConversationMessageContentType.Emoticon });
          }}
        />
        <ConversationAttachmentMenu
          ref={attachmentMenuRef}
          onPick={(fileType, menuType) => {
            form.setValue('attachmentType', fileType);
            return pickMedia({
              accept: fileType,
              type: menuType,
              limit: 1,
            });
          }}
          options={['camera', 'file', 'image', 'video']}
          isOpen={isAttachmentMenuOpen}
        />
        <input
          {...filesField}
          value={undefined}
          onChange={(e) => {
            const file = head(e.target.files ?? []);
            if (file) {
              const uploadableFile = convertFileToUploadable(file);
              const fileSizeLimit = getFileSizeLimit(form.getValues('attachmentType'), isInternalUser);
              if (file.size > fileSizeLimit) {
                addToast({
                  type: 'error',
                  message: t('errors.file-size-limit', { limit: readableFileSize(fileSizeLimit) }),
                });
                return;
              }
              onSubmit({
                attachment: uploadableFile,
                attachmentType: form.getValues('attachmentType'),
              });
            }
          }}
          type='file'
          ref={(r) => {
            filesField.ref(r);
            fileInputRef.current = r;
          }}
          className='hidden'
        />
      </div>
    </PageLayout>
  );
};
